import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
  Tag,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiClipboard } from "react-icons/fi";
import { useGetNeedQuery, type NeedDto, type NeedPatchDto } from "../../autogen/bff-api";
import { CpvSelector } from "../../common/cpv/CpvSelector";
import { useCpvCodes } from "../../common/cpv/useCpvCodes";
import { useEditor, useFieldErrorTranslation } from "../../common/editing/save-coordinator";
import { NumericInput } from "../../common/input/NumericInput";
import { LabelValueGrid } from "../sourcing-events/portal/view-single/LabelValueGrid";
import { SectionCard } from "../sourcing-events/portal/view-single/SectionCard";

const DASH = "–";
const nokFormatter = new Intl.NumberFormat("nb-NO");

export const NeedProcurerAssessment = ({ needId }: { needId: string }) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<NeedDto, NeedPatchDto>(needId);
  const formatFieldError = useFieldErrorTranslation();
  const { refetch } = useGetNeedQuery({ needId });
  const assessment = state.procurerAssessment;
  if (!assessment) return null;

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const cpvError = fieldError("procurerAssessment.cpvCodes");
  const valueError = fieldError("procurerAssessment.value");
  const value = assessment.value != null ? Number(assessment.value) : null;
  const movedOn = !!fieldError("procurerAssessment");

  return (
    <SectionCard icon={FiClipboard} title={t("Procurer's assessment")}>
      {movedOn && (
        <Alert status="warning" rounded="md" fontSize="sm" mb="4">
          <AlertIcon />
          <Stack spacing="2" align="flex-start">
            <Text>{t("The need has a new status, so the assessment can no longer be changed.")}</Text>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              {t("Show the need as it is now")}
            </Button>
          </Stack>
        </Alert>
      )}
      {assessment.canEdit && !movedOn ? (
        <Stack spacing="5">
          <FormControl isInvalid={!!cpvError}>
            <FormLabel>
              {t("CPV codes")} {isSaving("procurerAssessment.cpvCodes") && <Spinner size="xs" />}
            </FormLabel>
            <CpvSelector
              value={assessment.cpvCodes ?? []}
              onChange={(next) => updateField({ procurerAssessment: { cpvCodes: next } })}
              isInvalid={!!cpvError}
            />
            {cpvError && <FormErrorMessage>{formatFieldError(cpvError)}</FormErrorMessage>}
          </FormControl>
          <FormControl isInvalid={!!valueError}>
            <FormLabel>
              {t("Procurer's value")} {isSaving("procurerAssessment.value") && <Spinner size="xs" />}
            </FormLabel>
            <InputGroup>
              <NumericInput
                numericValue={value}
                placeholder="0"
                onNumericChange={(next) => updateField({ procurerAssessment: { value: next } }, { debounceMs: 300 })}
              />
              <InputRightAddon>{t("NOK")}</InputRightAddon>
            </InputGroup>
            {valueError ? (
              <FormErrorMessage>{formatFieldError(valueError)}</FormErrorMessage>
            ) : (
              <FormHelperText>
                {t("Your own estimate. The value the reporter entered stays as it is. The reporter does not see this section.")}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      ) : (
        <ReadOnlyAssessment cpvCodes={assessment.cpvCodes ?? []} value={value} />
      )}
    </SectionCard>
  );
};

const ReadOnlyAssessment = ({ cpvCodes, value }: { cpvCodes: string[]; value: number | null }) => {
  const { t, i18n } = useTranslation();
  const { codeIndex } = useCpvCodes();
  const isNorwegian = i18n.language.startsWith("no");
  const cpvLabel = (code: string) => {
    const dto = codeIndex[code];
    const name = dto ? (isNorwegian ? dto.nameNb : dto.nameEn) : "";
    return name ? `${code} — ${name}` : code;
  };

  return (
    <Stack spacing="5">
      <Stack spacing="1">
        <Text color="smMuted" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
          {t("CPV codes")}
        </Text>
        {cpvCodes.length > 0 ? (
          <Wrap>
            {cpvCodes.map((code) => (
              <WrapItem key={code}>
                <Tag colorScheme="teal" borderRadius="full">
                  <TagLabel whiteSpace="normal">{cpvLabel(code)}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        ) : (
          <Text color="smText" fontSize="sm">
            {DASH}
          </Text>
        )}
      </Stack>
      <LabelValueGrid
        rows={[{ label: t("Procurer's value"), value: value == null ? DASH : `${nokFormatter.format(value)} NOK` }]}
      />
    </Stack>
  );
};
